import { TabContext, TabPanel } from "@mui/lab";
import { Tabs, Tab, Box, Tooltip, IconButton, Typography, SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material";
import AddLinkIcon from '@mui/icons-material/AddLink';
import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { FC, useState } from "react";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import Scrollbar from "@/components/Scrollbar";
import { FunctionField } from "@/components/field";
import { RecPatch } from "@/dataProvider/tools";
import { ClientStatus, ClientManageDto, ClientType } from "@/gen/api/user";
import AddShareSecretClient from "./AddShareSecretClient";
import ClientsTable from "./ClientsTable";
import ResetSharedSecret from "./ResetSharedSecret";
import { ClientsManageResources, useClientManageContext } from "./ClientManageContext";
import { ListContextProvider, useSaveContext } from "ra-core";
import Confirm from "@/components/widgets/Confirm";
import CreateOrEdit from "@/controller/CreateOrEdit";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import EditClient from "./EditClient";
import EditClientReview from "./EditClientReview";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_ONDATA_CLIENTS } from "@/constants";
import AssignClientDatabaseFileTemplate from "./AssignClientDatabaseFileTemplate";
import Edit from "@/controller/Edit";
import { EncryptedDatabasesContextProvider } from "@/components/databases/useEncryptedDatabases";


const { clientsDriverRes } = ClientsManageResources

const DriverEncryptToolsList: FC<{
  openAdd:boolean,
  setOpenAdd: (x: boolean) => void,
}> = ({openAdd, setOpenAdd}) => {
  const [states, setStates] = useState({
    toReview: null as ClientManageDto|null,
    toRevoke: null as ClientManageDto|null,
    toResume: null as ClientManageDto|null,
    toViewDetail: null as ClientManageDto|null,
    toForceRefresh: null as ClientManageDto|null,
    toResetSharedSecred: null as ClientManageDto|null,
    toAssignObjs: null as ClientManageDto|null,
  })
  const { listCtrls:{drivers: clientsDriverCtrl}, states:{currTab}, setCurrTab } = useClientManageContext();
  const { setPage, setSort, refetch, } = clientsDriverCtrl

  const hasWritePermit = useHasPermit([MANAGE_ONDATA_CLIENTS])
  
  const keyRefreshHandlers = useDefaultSavedHandlers("Schedule Client Key Refresh")
  const assignObjsHandlers = useDefaultSavedHandlers("Assign Resources")
  
  const handleTabsChange = (event: any, newValue: string) => {
    setPage(1)
    setSort(
      newValue === (ClientStatus[ClientStatus.PendingApproval]) ? 'register_at'
      : newValue === (ClientStatus[ClientStatus.Enabled])  ? 'reviewed_at'
      : newValue === (ClientStatus[ClientStatus.Rejected]) ? 'reviewed_at'
      : newValue === (ClientStatus[ClientStatus.Suspend])  ? 'suspended_at'
      : newValue === (ClientStatus[ClientStatus.Revoked])  ? 'revoked_at'
      : 'id',
      'DESC'
    )
    setCurrTab(newValue)
  };

  return (
    <>
      <ListContextProvider value={clientsDriverCtrl}>
        <TabContext value={currTab}>
          <Tabs
            indicatorColor="secondary"
            onChange={handleTabsChange}
            scrollButtons="auto"
            textColor="secondary"
            value={currTab}
            variant="scrollable"
          >
            <Tab label="Registrations" value={ClientStatus[ClientStatus.PendingApproval]} />
            <Tab label="Active" value={ClientStatus[ClientStatus.Enabled]} />
            <Tab label="Suspended" value={ClientStatus[ClientStatus.Suspend]} />
            <Tab label="Rejected" value={ClientStatus[ClientStatus.Rejected]} />
            <Tab label="Revoked" value={ClientStatus[ClientStatus.Revoked]} />
          </Tabs>
          
          <Scrollbar options={{ suppressScrollY: true }}>
            <Box sx={{ minWidth: 1150, '& .MuiTabPanel-root': { p: 'unset' } }}>
              <TabPanel value={ClientStatus[ClientStatus.PendingApproval]} >
                <ClientsTable 
                  ActionField={
                    hasWritePermit &&
                    <FunctionField label="Actions" sortable={false}
                      render={(d?: ClientManageDto & RecPatch) => (
                        <>
                        <Tooltip title="Review">
                          <IconButton
                            aria-label="Review Client"
                            sx={{
                              color: 'primary.main' as any, 
                              '&:hover': { backgroundColor: 'action.hover' },
                            }}
                            onClick={() => { setStates(v => ({...v, toReview: d || null})) }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        </>
                      )}
                    />
                  }
                />
              </TabPanel>
              <TabPanel value={ClientStatus[ClientStatus.Enabled]} >
                <ClientsTable includeApproveTime={true} 
                  ActionField={
                    hasWritePermit &&
                    <FunctionField label="Actions" sortable={false}
                      render={(d?: ClientManageDto & RecPatch) => (
                        <>
                          <Box sx={{ position: 'relative', width: 32, height: 32 }}>
                            <SpeedDial
                              ariaLabel="Actions"
                              sx={{ position: 'absolute', top: -4, right: -22, mt: -1, scale: '0.8' }}
                              icon={<SpeedDialIcon />}
                              FabProps={{ size: 'small', color: 'default' }}
                              direction="left"
                            >
                              <SpeedDialAction
                                icon={<EditIcon />}
                                tooltipTitle="Edit Client"
                                onClick={() => { setStates(v => ({...v, toRevoke: d || null})) }}
                              />
                              <SpeedDialAction
                                icon={<AddLinkIcon />}
                                tooltipTitle="Assign Resources"
                                onClick={() => { setStates(v => ({...v, toAssignObjs: d || null})) }}
                              />
                              {d?.clientType === ClientType.DataDriver &&
                                <SpeedDialAction
                                  icon={<AutorenewIcon />}
                                  tooltipTitle="Force Refresh Key"
                                  onClick={() => { setStates(v => ({...v, toForceRefresh: d || null})) }}
                                />}
                              {(d?.clientType === ClientType.ShareSecretDataDriver || d?.clientType === ClientType.ShareSecretEncryptionTool) &&
                                <SpeedDialAction
                                  icon={<AutorenewIcon />}
                                  tooltipTitle="Reset Client Secret"
                                  onClick={() => { setStates(v => ({...v, toResetSharedSecred: d || null})) }}
                                />}
                            </SpeedDial>
                          </Box>
                        </>
                      )}
                    />
                  }
                />
              </TabPanel>
              <TabPanel value={ClientStatus[ClientStatus.Suspend]} >
                <ClientsTable includeApproveTime={true} includeSuspendTime={true}
                  ActionField={
                    hasWritePermit &&
                    <FunctionField label="Actions" sortable={false}
                      render={(d?: ClientManageDto & RecPatch) => (
                        <Tooltip title="Edit">
                          <IconButton 
                            aria-label="Edit Client"
                            onClick={(e:any) => { setStates(v => ({...v, toResume: d || null})) }}
                            sx={{ 
                              color: 'primary.main' as any, 
                              '&:hover': { backgroundColor: 'action.hover' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />
                  }
                />
              </TabPanel>
              <TabPanel value={ClientStatus[ClientStatus.Rejected]} >
                <ClientsTable includeRejectTime={true} 
                  selectRow={(x:any) => { hasWritePermit && setStates(v => ({...v, toViewDetail: x}))}}
                />
              </TabPanel>
              <TabPanel value={ClientStatus[ClientStatus.Revoked]} >
                <ClientsTable includeApproveTime={true} includeRevokeTime={true} 
                  selectRow={(x:any) => { hasWritePermit && setStates(v => ({...v, toViewDetail: x}))}}
                />
              </TabPanel>
            </Box>
          </Scrollbar>
          {//@ts-ignore 
          <DefaultPagination {...clientsDriverCtrl} />}
          <AddShareSecretClient open={openAdd} setOpen={setOpenAdd} />
          <ResetSharedSecret client={states.toResetSharedSecred} clear={() => setStates(v => ({...v, toResetSharedSecred: null}))} />
        </TabContext>
      </ListContextProvider>

      <CreateOrEdit resource={clientsDriverRes} basePath="" mutationMode="pessimistic" 
        id={(states.toReview || states.toRevoke || states.toResume)?.clientId || undefined}  
        record={states.toReview || states.toRevoke || states.toResume || states.toViewDetail}
        onSuccess={refetch}
      >
        <EditClientReview open={!!states.toReview} onClose={()=>{ setStates(v => ({...v, toReview: null })) }} />
        {hasWritePermit &&
          <EditClient 
            open={!!states.toRevoke || !!states.toResume || !!states.toViewDetail} 
            onClose={()=>{ setStates(v => ({...v, toRevoke: null, toResume: null, toViewDetail: null })) }} 
          />}
      </CreateOrEdit>
      {hasWritePermit &&
        <>
          <CreateOrEdit resource="settings-client-force-refresh-key" basePath="" mutationMode="pessimistic" record={states.toForceRefresh}
            {...keyRefreshHandlers}
          >
            <ConfirmForceClientKeyRefresh 
              targetClient={states.toForceRefresh} 
              onClose={() => setStates(v => ({...v, toForceRefresh: null}))}
            />
          </CreateOrEdit>
          <EncryptedDatabasesContextProvider>
            <Edit basePath="" mutationMode="pessimistic" {...assignObjsHandlers}
              resource={states.toAssignObjs ? 'client-access-policy' : 'noop'} 
              id={states.toAssignObjs?.clientId ?? -1} 
            >
              <AssignClientDatabaseFileTemplate open={!!states.toAssignObjs} onClose={()=>{ setStates(v => ({...v, toAssignObjs: null }))}} client={states.toAssignObjs} />
            </Edit>
          </EncryptedDatabasesContextProvider>
        </>
        }
    </>
  )
}

export default DriverEncryptToolsList;


const ConfirmForceClientKeyRefresh: FC<{ targetClient: ClientManageDto|null, onClose: ()=>void }> = ({
  targetClient,
  onClose,
}) => {
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  const handleDelete = () => {
    targetClient && save(targetClient, { onSuccess: onClose })
  }
  return (
    <Confirm
      isOpen={!!targetClient}
      loading={saving}
      title={"Force Refresh Key"}
      onConfirm={handleDelete}
      onClose={onClose}
      confirm="Confirm"
      cancel="Cancel"
      content={(
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Force refresh client's key will immediately invalidate client's previous key, 
              the whole process is managed by OnData automatically. Do you wish to force refresh 
              the key of client "{targetClient?.clientName}"?
            </Typography>
          </Box>
        </Box>
      )}
    />
  )
}
